/**
 * Debit order mandates, from Orchid's side.
 *
 *   "be able to reproduce the records of mandate permissions given by clients
 *    and their digital signatures given, with an audit trail and all attached
 *    user identities, and be able to export that from the Portal in case the
 *    bank needs, and also be able to download newly signed mandates in csv
 *    format ... in order to add it to our collections dashboard on FNB online
 *    banking enterprise."
 *
 * TWO READS THAT LOOK SIMILAR AND ARE NOT THE SAME ACT AT ALL.
 *
 *   fn_mandate_evidence_pack answers "did this person agree, and to what". It
 *   carries the signature, the exact wording they were shown, the device and
 *   address they signed from, their OFX Code, and every event since -- and NO
 *   bank details beyond a last4. A dispute is about consent, not about an
 *   account number, and decrypting for a question that does not need the answer
 *   is how a habit starts.
 *
 *   fn_export_mandates_for_the_bank DECRYPTS. It hands back real ID numbers and
 *   real account numbers, because FNB cannot load a mandate from a mask. It
 *   needs MFA, a permission granted only through a time-boxed role, and a
 *   stated reason -- and it writes a line to every mandate's history BEFORE it
 *   returns, so a decryption that happened is on the record even if the
 *   download never reached anybody.
 *
 * WHICH IS WHY THIS FILE HAS NO "JUST LOOK AT IT" CALL. There is no function
 * here that returns a decrypted number without a reason, because there is no
 * such function in the database either.
 */

import { supabase, isSupabaseConfigured } from './supabaseClient';

const NOT_CONFIGURED =
  'This portal was built without its Supabase credentials, so it cannot reach the database.';

const fail = (error) => ({ data: null, error });

function readable(error) {
  if (!error) return null;
  // A database that has not had script 128 run. Said plainly: "could not find
  // the function" reads to a finance officer as though they did something
  // wrong.
  if (error.code === 'PGRST202') {
    return 'Debit order mandates are not switched on for this database yet. '
         + 'If you are the administrator, script 128 has not been run.';
  }
  if (error.code === '42501') return 'Your account is not permitted to do that.';
  // The database's own sentences are written to be read -- "drawing an evidence
  // pack needs a stated reason", "no mandate encryption key is configured" --
  // so they pass through rather than being replaced with something vaguer.
  return error.message || 'Something went wrong.';
}

/**
 * One mandate, everything about how it was agreed to.
 *
 * The reason is required by the database and is written to the mandate's own
 * history, so "who looked at this, and why" is answerable for ever.
 */
export async function evidencePack(mandateReference, reason) {
  if (!isSupabaseConfigured) return fail(NOT_CONFIGURED);
  const { data, error } = await supabase.rpc('fn_mandate_evidence_pack', {
    p_mandate_reference: mandateReference,
    p_reason: reason,
  });
  if (error) return fail(readable(error));
  return { data: (data && data[0]) || null, error: null };
}

/**
 * The mandates FNB needs, decrypted, with the reason on the record.
 *
 * p_from and p_to bound it to a period -- normally "since the last upload" --
 * because an export of every mandate Orchid has ever held, every time somebody
 * wants this week's new ones, is a decryption of the whole book to answer a
 * question about five rows.
 */
/**
 * THE DAY YOU NAME IS A DAY YOU GET.
 *
 * The founder, 16 September: a mandate signed that morning did not come out in
 * the export. `fn_export_mandates_for_the_bank` bounds the period with
 * `m.signed_at < p_to` -- a half-open interval, which is the right convention
 * for a database and the wrong thing to hand a date picker.
 *
 * `<input type="date">` yields `2026-09-16`, which becomes MIDNIGHT. So asking
 * for mandates "to the 16th" excluded every mandate signed on the 16th, and the
 * export said "No mandates were signed in that period" -- a sentence written to
 * reassure, doing the opposite of its job.
 *
 * The interval stays half-open, because that is correct and because changing it
 * would need a migration to a function the bank export depends on. What changes
 * is that this turns the day a person NAMED into the exclusive bound that
 * includes it: the next midnight.
 *
 * It is done on a plain date string rather than through `Date`, deliberately.
 * `new Date('2026-09-16')` parses as UTC while `new Date(2026, 8, 16)` parses as
 * local, and mixing them is how an off-by-one becomes an off-by-one-in-some-
 * timezones. Adding a day to `[y, m, d]` with `Date.UTC` and reading it back
 * with the UTC getters never touches local time at all.
 */
export function theDayAfter(day) {
  if (!day) return null;
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day).trim());
  // Not a plain date -- a full timestamp, or something unexpected. Passed
  // through untouched rather than guessed at: a wrong bound silently dropping
  // rows is the defect this function exists to fix, and inventing one here
  // would be the same mistake in a new place.
  if (!parts) return day;
  const next = new Date(Date.UTC(
    Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]) + 1,
  ));
  const pad = (n) => String(n).padStart(2, '0');
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
}

export async function exportForTheBank({ reason, from = null, to = null }) {
  if (!isSupabaseConfigured) return fail(NOT_CONFIGURED);
  const { data, error } = await supabase.rpc('fn_export_mandates_for_the_bank', {
    p_reason: reason,
    p_from: from,
    p_to: theDayAfter(to),
  });
  if (error) return fail(readable(error));
  return { data: data || [], error: null };
}

// ---------------------------------------------------------------------------
// THE FILE FNB LOADS
// ---------------------------------------------------------------------------

/**
 * The columns, in the order the collections dashboard expects them.
 *
 * Held as a list rather than built inline, so the header row and the value row
 * cannot drift apart -- which is the classic CSV defect and the one that is
 * hardest to see, because the file still opens and every column is simply
 * describing the wrong thing.
 */
export const CSV_COLUMNS = [
  ['mandate_reference', 'Contract Reference'],
  ['account_holder_name', 'Account Holder'],
  ['id_number', 'Identity Number'],
  ['bank_name', 'Bank'],
  ['branch_code', 'Branch Code'],
  ['account_number', 'Account Number'],
  ['account_type', 'Account Type'],
  ['amount_ceiling_zar', 'Maximum Collection Amount'],
  ['collection_day', 'Collection Day'],
  ['commencement_date', 'Commencement Date'],
  ['abbreviated_name', 'Abbreviated Name'],
  ['signed_at', 'Mandate Signed'],
  ['signature_method', 'Signature Type'],
  ['ofx_connect_code', 'Orchid Reference'],
];

/**
 * ESCAPE EVERYTHING, ALWAYS.
 *
 * A South African account holder name containing a comma -- "Mokoena, T" -- or
 * a business name with a quote in it silently shifts every column after it by
 * one, and a bank loads the branch code as an account number. Quoting every
 * field costs nothing and removes the whole class.
 *
 * AND A LEADING APOSTROPHE ON ANYTHING NUMERIC-LOOKING would be Excel's fault
 * rather than ours; that is deliberately NOT done here, because the file is
 * for a bank's loader and not for a spreadsheet, and a stray apostrophe in an
 * account number is worse than a spreadsheet dropping a leading zero.
 */
function csvCell(value) {
  if (value === null || value === undefined) return '""';
  return `"${String(value).replace(/"/g, '""')}"`;
}

export function toCsv(rows) {
  const header = CSV_COLUMNS.map(([, label]) => csvCell(label)).join(',');
  const body = (rows || []).map(
    (row) => CSV_COLUMNS.map(([key]) => csvCell(row[key])).join(','),
  );
  // CRLF, which is what the CSV specification says and what a bank's loader is
  // likeliest to have been written against.
  return [header, ...body].join('\r\n');
}

/**
 * Hand the file over.
 *
 * A Blob and an object URL rather than a data: URI: an export of a few hundred
 * mandates exceeds what some browsers accept in an address, and the failure
 * mode there is a download that silently does nothing.
 */
export function downloadCsv(text, filename) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoked on the next tick rather than immediately: revoking before the
  // browser has started reading the blob cancels the download in some of them.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** ofx-mandates-2026-08-24.csv — sortable, and says what it is. */
export function csvFilename(now = new Date()) {
  return `ofx-mandates-${now.toISOString().slice(0, 10)}.csv`;
}
